/**
 * MIMOYA Theme JavaScript Framework (V1.1.1)
 * Handles Header Search Expand, Sticky Header Shadow, Mobile Menu Drawer,
 * Cart Drawer & Cart Page with Shopify Section Rendering API,
 * AJAX Add-to-Cart with button state preservation,
 * and Shopify-compatible money formatting.
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeaderSearchExpand();
  initStickyHeaderShadow();
  initMobileMenu();
  initCartDrawer();
  initCartPageForm();
  initCollectionSortAutoSubmit();
  initAjaxForms();
  bindCartItemInteractions();
  initRelatedProducts();
});

// Restore from the back/forward cache: the cart may have changed in the meantime.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartData();
});

/* Theme Editor Section Reload Handler: re-runs initializers scoped to just
   the reloaded section (event.target), never the full document. */
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!section) return;
  if (section.querySelector('form[action*="/cart/add"]')) initAjaxForms(section);
  if (section.querySelector('.collection-sort__select')) initCollectionSortAutoSubmit(section);
  if (section.querySelector('[data-product-recommendations]')) initRelatedProducts(section);
  if (section.querySelector('[data-header-search-expand]')) initHeaderSearchExpand();
  if (section.querySelector('[data-mobile-menu-toggle]')) initMobileMenu(section);
  if (section.id === 'shopify-section-header') initStickyHeaderShadow();
  if (section.querySelector('[data-cart-drawer]')) {
    initCartDrawer();
    bindCartItemInteractions(section);
  }
  if (section.querySelector('[data-cart-page]')) {
    initCartPageForm(section);
    bindCartItemInteractions(section);
  }
});

/* 1. Shopify-Compatible Money Formatter */
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || (window.MIMOYA && window.MIMOYA.moneyFormat) || '${{amount}}';

  function defaultOption(opt, def) {
    return (typeof opt === 'undefined') ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) {
      return '0';
    }

    const numStr = (number / 100.0).toFixed(precision);
    const parts = numStr.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsPart = parts[1] ? (decimal + parts[1]) : '';

    return dollars + centsPart;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) {
    return formatWithDelimiters(cents, 2);
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

/* 2. Accessible Focus Trap for Dialogs / Drawers */
function trapFocus(container, event) {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(
    container.querySelectorAll(
      'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => {
    return el.offsetParent !== null && window.getComputedStyle(el).visibility !== 'hidden';
  });

  if (!focusable.length) return;

  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];

  if (event.shiftKey) {
    if (document.activeElement === firstFocusable || !container.contains(document.activeElement)) {
      event.preventDefault();
      lastFocusable.focus();
    }
  } else {
    if (document.activeElement === lastFocusable || !container.contains(document.activeElement)) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }
}

/* 3. Header Search: icon button that expands into an inline field (desktop) */
function getHeaderSearchEls() {
  return {
    wrapper: document.querySelector('[data-header-search-expand]'),
    toggleBtn: document.querySelector('[data-header-search-toggle]'),
    panel: document.querySelector('[data-header-search-panel]'),
    input: document.querySelector('[data-header-search-input]')
  };
}

function openHeaderSearch() {
  const { wrapper, toggleBtn, panel, input } = getHeaderSearchEls();
  if (!wrapper || !toggleBtn || !panel || !input) return;
  wrapper.classList.add('is-open');
  toggleBtn.setAttribute('aria-expanded', 'true');
  panel.removeAttribute('aria-hidden');
  Array.from(panel.querySelectorAll('input, button')).forEach(el => el.removeAttribute('tabindex'));
  input.focus();
}

function closeHeaderSearch(restoreFocus) {
  const { wrapper, toggleBtn, panel } = getHeaderSearchEls();
  if (!wrapper || !toggleBtn || !panel) return;
  wrapper.classList.remove('is-open');
  toggleBtn.setAttribute('aria-expanded', 'false');
  if (panel.contains(document.activeElement)) {
    document.activeElement.blur();
  }
  panel.setAttribute('aria-hidden', 'true');
  Array.from(panel.querySelectorAll('input, button')).forEach(el => el.setAttribute('tabindex', '-1'));
  if (restoreFocus) toggleBtn.focus();
}

function initHeaderSearchExpand() {
  const { wrapper, toggleBtn, panel, input } = getHeaderSearchEls();
  if (!wrapper || !toggleBtn || !panel || !input) return;

  if (!window.__mimoyaSearchDelegatesBound) {
    window.__mimoyaSearchDelegatesBound = true;

    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-header-search-toggle]');
      const current = getHeaderSearchEls();
      if (toggle && current.wrapper) {
        if (current.wrapper.classList.contains('is-open')) {
          closeHeaderSearch(true);
        } else {
          openHeaderSearch();
        }
        return;
      }
      if (current.wrapper && current.wrapper.classList.contains('is-open') && !current.wrapper.contains(e.target)) {
        closeHeaderSearch(false);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const { panel: currentPanel } = getHeaderSearchEls();
      if (currentPanel && currentPanel.contains(e.target)) closeHeaderSearch(true);
    });

    document.addEventListener('focusout', (e) => {
      const { wrapper: currentWrapper } = getHeaderSearchEls();
      if (currentWrapper && currentWrapper.classList.contains('is-open') && !currentWrapper.contains(e.relatedTarget)) {
        closeHeaderSearch(false);
      }
    });
  }

  closeHeaderSearch(false);
}

/* 4. Sticky Header Shadow */
let stickyHeaderObserver = null;

let announceResizeBound = false;
let announceObserver = null;
// The announcement bar scrolls away while the nav bar stays pinned: the sticky offset is minus the bar's height.
function syncAnnouncementHeight() {
  const bar = document.querySelector('.m-header__announcement');
  const h = bar ? bar.offsetHeight : 0;
  document.documentElement.style.setProperty('--m-announce-h', h + 'px');
  const sentinel = document.querySelector('[data-sticky-header-sentinel]');
  if (sentinel) sentinel.style.top = h + 'px';
}

function initStickyHeaderShadow() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  syncAnnouncementHeight();
  if (!announceResizeBound) {
    announceResizeBound = true;
    window.addEventListener('resize', syncAnnouncementHeight);
  }
  if ('ResizeObserver' in window) {
    if (announceObserver) announceObserver.disconnect();
    const bar = document.querySelector('.m-header__announcement');
    if (bar) {
      announceObserver = new ResizeObserver(syncAnnouncementHeight);
      announceObserver.observe(bar);
    }
  }
  if (!('IntersectionObserver' in window)) return;

  if (stickyHeaderObserver) {
    stickyHeaderObserver.disconnect();
    stickyHeaderObserver = null;
  }

  document.querySelectorAll('[data-sticky-header-sentinel]').forEach((el) => el.remove());

  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.setAttribute('data-sticky-header-sentinel', '');
  sentinel.style.cssText = 'position:absolute;top:' + (document.querySelector('.m-header__announcement') ? document.querySelector('.m-header__announcement').offsetHeight : 0) + 'px;left:0;width:1px;height:1px;pointer-events:none;';
  header.parentNode.insertBefore(sentinel, header);

  stickyHeaderObserver = new IntersectionObserver(
    ([entry]) => header.classList.toggle('is-stuck', !entry.isIntersecting),
    { threshold: 0 }
  );
  stickyHeaderObserver.observe(sentinel);
}

/* 5. Mobile Menu Drawer */
let mobileMenuKeydownBound = false;

function initMobileMenu(root = document) {
  const toggleBtn = root.querySelector('[data-mobile-menu-toggle]');
  const closeBtn = root.querySelector('[data-mobile-menu-close]');
  const drawer = root.querySelector('[data-mobile-menu-drawer]');

  if (!toggleBtn || !drawer) return;

  let previousActiveElement = null;

  const openMenu = () => {
    previousActiveElement = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const content = drawer.querySelector('.mobile-drawer__content');
    window.setTimeout(() => { if (content) content.focus(); }, 60);
  };

  const closeMenu = () => {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    if (previousActiveElement && previousActiveElement.focus) {
      previousActiveElement.focus();
    }
    if (document.activeElement && drawer.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    drawer.setAttribute('aria-hidden', 'true');
  };

  toggleBtn.addEventListener('click', openMenu);
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);

  drawer.addEventListener('click', (e) => {
    if (e.target === drawer) closeMenu();
    if (e.target.closest('.mobile-drawer__link')) closeMenu();
  });

  drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
    trapFocus(drawer, e);
  });

  window.MIMOYA_closeMobileMenu = closeMenu;
  if (!mobileMenuKeydownBound) {
    mobileMenuKeydownBound = true;
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (window.MIMOYA_closeMobileMenu) window.MIMOYA_closeMobileMenu();
    });
  }
}

/* 6. Section Rendering API & Cart State Management */
let cartRefreshSeq = 0;
const cartItemQuantityTimers = new Map();

function getCartSectionIds() {
  const sections = ['cart-drawer'];
  const pageEl = document.querySelector('[data-cart-page]');
  if (pageEl && pageEl.dataset.sectionId) {
    if (!sections.includes(pageEl.dataset.sectionId)) {
      sections.push(pageEl.dataset.sectionId);
    }
  }
  return sections;
}

function getCartStrings() {
  const strings = Object.assign({}, (window.MIMOYA && window.MIMOYA.strings) || {});
  const scriptTag = document.querySelector('[data-cart-strings]');
  if (scriptTag) {
    try {
      const parsed = JSON.parse(scriptTag.textContent);
      Object.assign(strings, parsed);
    } catch (e) {}
  }
  return strings;
}

function announceCart(message) {
  if (!message) return;
  let region = document.getElementById('MimoyaLiveRegion');
  if (!region) {
    region = document.createElement('div');
    region.id = 'MimoyaLiveRegion';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  setTimeout(() => { region.textContent = message; }, 50);
}

function captureCartFocus(container) {
  const active = document.activeElement;
  if (!active || !container || !container.contains(active)) return () => {};
  const item = active.closest('[data-cart-item-key]');
  const key = item && item.dataset.cartItemKey;
  const control = ['[data-qty-dec]', '[data-qty-inc]', '[data-qty-val]', '[data-cart-item-remove]']
    .find(sel => active.matches(sel));
  if (!key || !control) return () => {};
  return () => {
    const next = Array.from(container.querySelectorAll('[data-cart-item-key]'))
      .find(el => el.dataset.cartItemKey === key);
    const target = next && next.querySelector(control);
    if (target) {
      target.focus({ preventScroll: true });
      return;
    }
    // The line was removed: move focus to the next remove button, else the dialog close button.
    const fallback = container.querySelector('[data-cart-item-remove]') || container.querySelector('[data-cart-drawer-close]');
    if (fallback) fallback.focus({ preventScroll: true });
  };
}

function applyCartSections(sections) {
  if (!sections) return;
  const parser = new DOMParser();

  // 1. Drawer inner contents
  if (sections['cart-drawer']) {
    const drawerDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');
    const incomingInner = drawerDoc.querySelector('[data-cart-drawer-inner]');
    const liveDrawerInner = document.querySelector('[data-cart-drawer-inner]');
    const drawer = document.querySelector('[data-cart-drawer]');
    if (incomingInner && liveDrawerInner && drawer) {
      const restoreFocus = captureCartFocus(drawer);
      liveDrawerInner.innerHTML = incomingInner.innerHTML;
      initAjaxForms(liveDrawerInner);
      bindCartItemInteractions(liveDrawerInner);
      restoreFocus();
    }
  }

  // 2. Cart page root children
  const pageEl = document.querySelector('[data-cart-page]');
  if (pageEl) {
    const sectionId = pageEl.dataset.sectionId;
    const pageHtml = (sectionId && sections[sectionId]) || sections['main-cart'] || sections['main'];
    if (pageHtml) {
      const pageDoc = parser.parseFromString(pageHtml, 'text/html');
      const incomingPage = pageDoc.querySelector('[data-cart-page]');
      if (incomingPage) {
        const restoreFocus = captureCartFocus(pageEl);
        pageEl.innerHTML = incomingPage.innerHTML;
        initAjaxForms(pageEl);
        bindCartItemInteractions(pageEl);
        initCartPageForm(pageEl);
        restoreFocus();
      }
    }
  }
}

function updateCartState(cart) {
  if (!cart) return;

  const countBadges = document.querySelectorAll('[data-cart-count]');
  countBadges.forEach(badge => {
    badge.textContent = cart.item_count;
    badge.hidden = cart.item_count === 0;
  });

  const cartLink = document.querySelector('[data-cart-drawer-toggle]');
  const strings = getCartStrings();
  if (cartLink) {
    const ariaLabel = strings.cart_aria_label || '';
    const suffix = cart.item_count === 1
      ? (strings.cart_items_count_suffix_one || '')
      : (strings.cart_items_count_suffix || '');
    cartLink.setAttribute('aria-label', `${ariaLabel} (${cart.item_count} ${suffix})`);
  }

  document.dispatchEvent(new CustomEvent('mimoya:cart:updated', { detail: { cart } }));
}

async function refreshCartData() {
  const seq = ++cartRefreshSeq;
  try {
    const sections = getCartSectionIds();
    const url = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url)
      ? window.MIMOYA.routes.cart_url + '/update.js'
      : '/cart/update.js';

    let cart = null;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        sections: sections,
        sections_url: window.location.pathname
      })
    });

    if (res.ok) {
      cart = await res.json();
    } else {
      const cartUrl = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url)
        ? window.MIMOYA.routes.cart_url + '.js'
        : '/cart.js';
      const cartRes = await fetch(cartUrl);
      if (!cartRes.ok) return false;
      cart = await cartRes.json();
      const secRes = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
      if (secRes.ok) {
        cart.sections = await secRes.json();
      }
    }

    if (seq !== cartRefreshSeq) return true;

    if (cart.sections) {
      applyCartSections(cart.sections);
    }
    updateCartState(cart);

    return true;
  } catch (err) {
    return false;
  }
}

// Re-queries the error regions: they live inside re-rendered markup, so call this after any refresh.
function showCartError(message) {
  document.querySelectorAll('[data-cart-drawer-error], [data-cart-page-error]').forEach((el) => {
    el.textContent = message;
    el.style.display = message ? 'block' : 'none';
  });
}

let cartChangeSeq = 0;

async function updateCartItemQuantity(key, quantity) {
  const changeSeq = ++cartChangeSeq;
  const drawerError = document.querySelector('[data-cart-drawer-error]');
  const pageError = document.querySelector('[data-cart-page-error]');
  const strings = getCartStrings();
  const sections = getCartSectionIds();

  try {
    const url = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_change_url)
      ? window.MIMOYA.routes.cart_change_url + '.js'
      : '/cart/change.js';

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: sections,
        sections_url: window.location.pathname
      })
    });

    if (response.ok) {
      const cart = await response.json();
      if (drawerError) {
        drawerError.textContent = '';
        drawerError.style.display = 'none';
      }
      if (pageError) {
        pageError.textContent = '';
        pageError.style.display = 'none';
      }

      // A newer change is in flight: let its (fresher) response render instead.
      if (changeSeq === cartChangeSeq && cart.sections) {
        applyCartSections(cart.sections);
      }
      updateCartState(cart);

      if (quantity === 0) {
        if (!document.querySelector('[data-cart-drawer].is-open')) announceCart(strings.item_removed);
        const statusEl = document.querySelector('[data-cart-drawer-status]');
        if (statusEl) {
          const msg = strings.item_removed || '';
          statusEl.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5F6B57" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 13c2 1.6 3.6 3.4 5 5.4 3-5.4 6.6-9.6 11-13"/></svg><span>${escapeHtml(msg)}</span>`;
          statusEl.style.display = 'flex';
        }
      }
      return true;
    } else {
      const errorData = await response.json().catch(() => ({}));
      const errMsg = errorData.description || errorData.message || strings.cart_error || '';
      await refreshCartData();
      showCartError(errMsg);
      return false;
    }
  } catch (err) {
    await refreshCartData();
    showCartError(strings.general_error || '');
    return false;
  }
}

function removeCartItem(key) {
  const existingTimer = cartItemQuantityTimers.get(key);
  if (existingTimer) {
    clearTimeout(existingTimer);
    cartItemQuantityTimers.delete(key);
  }
  return updateCartItemQuantity(key, 0);
}

function bindCartItemInteractions(root) {
  const scope = root || document;
  scope.querySelectorAll('[data-cart-item-key]').forEach(item => {
    const key = item.dataset.cartItemKey;
    const decBtn = item.querySelector('[data-qty-dec]');
    const incBtn = item.querySelector('[data-qty-inc]');
    const input = item.querySelector('[data-qty-val]');
    const removeBtn = item.querySelector('[data-cart-item-remove]');

    if (input) {
      const getQty = () => Math.max(1, parseInt(input.value, 10) || 1);
      const scheduleUpdate = (qty) => {
        const existing = cartItemQuantityTimers.get(key);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(() => {
          cartItemQuantityTimers.delete(key);
          updateCartItemQuantity(key, qty);
        }, 450);
        cartItemQuantityTimers.set(key, timer);
      };

      if (decBtn) {
        decBtn.disabled = getQty() <= 1;
        decBtn.addEventListener('click', () => {
          const next = Math.max(1, getQty() - 1);
          input.value = next;
          decBtn.disabled = next <= 1;
          scheduleUpdate(next);
        });
      }

      if (incBtn) {
        incBtn.addEventListener('click', () => {
          const next = getQty() + 1;
          input.value = next;
          if (decBtn) decBtn.disabled = false;
          scheduleUpdate(next);
        });
      }

      input.addEventListener('change', () => {
        const next = getQty();
        input.value = next;
        if (decBtn) decBtn.disabled = next <= 1;
        scheduleUpdate(next);
      });
    }

    if (removeBtn) {
      const handleRemove = (e) => {
        e.preventDefault();
        removeCartItem(key);
      };
      removeBtn.addEventListener('click', handleRemove);
      removeBtn.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          handleRemove(e);
        }
      });
    }
  });
}

/* 7. Cart Drawer Open / Close / Focus Management */
let cartDrawerKeydownBound = false;

function initCartDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  if (drawer.dataset.drawerBound === 'true') return;
  drawer.dataset.drawerBound = 'true';

  let previousActiveElement = null;

  const openCart = async (alreadyRefreshed = false) => {
    const success = alreadyRefreshed || await refreshCartData();
    if (success) {
      previousActiveElement = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => {
        el.setAttribute('aria-expanded', 'true');
      });
      document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

      // The drawer is visibility:hidden until its transition starts, so focus on the next frame.
      const content = drawer.querySelector('[data-cart-drawer-inner]');
      const focusTarget = drawer.querySelector('[data-cart-drawer-close]') || content;
      window.setTimeout(() => { if (focusTarget) focusTarget.focus(); }, 60);
    } else {
      window.location.href = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url) || '/cart';
    }
  };

  const closeCart = () => {
    if (!drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    if (previousActiveElement && previousActiveElement.focus) {
      previousActiveElement.focus();
      previousActiveElement = null;
    }
    if (document.activeElement && drawer.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    drawer.setAttribute('aria-hidden', 'true');
  };

  if (!window.__mimoyaCartToggleBound) {
    window.__mimoyaCartToggleBound = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-cart-drawer-toggle]');
      if (!btn) return;
      e.preventDefault();
      if (window.MIMOYA && window.MIMOYA.cartType === 'page') {
        window.location.href = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url) || '/cart';
      } else if (window.MIMOYA_openCartDrawer) {
        window.MIMOYA_openCartDrawer();
      }
    });
  }

  drawer.addEventListener('click', (e) => {
    if (e.target === drawer || e.target.closest('[data-cart-drawer-close]')) closeCart();
  });

  drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCart();
    trapFocus(drawer, e);
  });

  if (!cartDrawerKeydownBound) {
    cartDrawerKeydownBound = true;
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const currentDrawer = document.querySelector('[data-cart-drawer]');
      if (currentDrawer && currentDrawer.classList.contains('is-open')) {
        if (window.MIMOYA_closeCartDrawer) {
          window.MIMOYA_closeCartDrawer();
        } else {
          closeCart();
        }
      }
    });
  }

  window.MIMOYA_openCartDrawer = openCart;
  window.MIMOYA_closeCartDrawer = closeCart;
  window.MIMOYA_refreshCartData = refreshCartData;
}

/* 8. Cart Page Form */
function initCartPageForm(root = document) {
  const form = root.querySelector('[data-cart-page-form]');
  if (!form || form.dataset.pageFormBound) return;
  form.dataset.pageFormBound = 'true';
  form.addEventListener('submit', (e) => e.preventDefault());
}

/* 9. Collection Sort Auto-submit */
function initCollectionSortAutoSubmit(root = document) {
  root.querySelectorAll('.collection-sort__select').forEach(select => {
    select.addEventListener('change', () => {
      if (window.matchMedia('(max-width: 860px)').matches) {
        select.form.submit();
      }
    });
  });
}

/* 10. AJAX Add to Cart */
function initAjaxForms(root = document) {
  root.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    if (form.dataset.ajaxBound) return;
    form.dataset.ajaxBound = 'true';

    if (form.querySelector('[data-variant-quantity]')) {
      form.classList.add('is-multi-variant-enhanced');
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      const feedbackMsg = form.querySelector('[data-cart-feedback]');
      const strings = getCartStrings();
      const addLabelEl = submitBtn && submitBtn.querySelector('[data-add-label]');

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.origHtml = submitBtn.innerHTML;
        const addingText = strings.adding_to_cart || '';
        if (addLabelEl) {
          addLabelEl.textContent = addingText;
        } else {
          submitBtn.textContent = addingText;
        }
      }

      if (feedbackMsg) {
        feedbackMsg.textContent = '';
        feedbackMsg.className = 'status-msg';
        feedbackMsg.style.display = 'none';
      }

      try {
        const variantQuantityInputs = Array.from(form.querySelectorAll('[data-variant-quantity]'));
        const cartAddUrl = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_add_url)
          ? window.MIMOYA.routes.cart_add_url + '.js'
          : '/cart/add.js';

        let body;
        const headers = { 'Accept': 'application/json' };

        if (variantQuantityInputs.length) {
          const items = variantQuantityInputs
            .filter(input => !input.disabled && parseInt(input.value || '0', 10) > 0)
            .map(input => ({
              id: parseInt(input.dataset.variantId, 10),
              quantity: parseInt(input.value, 10)
            }));

          if (!items.length) return;
          body = JSON.stringify({ items });
          headers['Content-Type'] = 'application/json';
        } else {
          body = new FormData(form);
        }

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          body,
          headers
        });

        if (response.ok) {
          await response.json();

          if (feedbackMsg) {
            feedbackMsg.textContent = strings.added_to_cart || '';
            feedbackMsg.className = 'status-msg status-msg--success';
            feedbackMsg.style.display = 'block';
            setTimeout(() => { feedbackMsg.style.display = 'none'; }, 3000);
          }

          if (document.querySelector('[data-cart-page]')) {
            await refreshCartData();
          } else if (window.MIMOYA.cartType === 'page') {
            window.location.href = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url) || '/cart';
          } else {
            const refreshed = await refreshCartData();
            if (refreshed && window.MIMOYA_openCartDrawer) {
              await window.MIMOYA_openCartDrawer(true);
              const statusEl = document.querySelector('[data-cart-drawer-status]');
              if (statusEl) {
                const msg = strings.popped_into_bag || '';
                statusEl.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5F6B57" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 13c2 1.6 3.6 3.4 5 5.4 3-5.4 6.6-9.6 11-13"/></svg><span>${escapeHtml(msg)}</span>`;
                statusEl.style.display = 'flex';
              }
            } else {
              window.location.href = (window.MIMOYA && window.MIMOYA.routes && window.MIMOYA.routes.cart_url) || '/cart';
            }
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errMsg = errorData.description || errorData.message || strings.cart_error || '';

          if (feedbackMsg) {
            feedbackMsg.textContent = errMsg;
            feedbackMsg.className = 'status-msg status-msg--error';
            feedbackMsg.style.display = 'block';
          }
        }
      } catch (err) {
        if (feedbackMsg) {
          feedbackMsg.textContent = strings.general_error || '';
          feedbackMsg.className = 'status-msg status-msg--error';
          feedbackMsg.style.display = 'block';
        }
      } finally {
        if (submitBtn) {
          const hasVariantQuantities = form.querySelectorAll('[data-variant-quantity]').length > 0;
          const selectedQuantity = Array.from(form.querySelectorAll('[data-variant-quantity]:not(:disabled)'))
            .reduce((sum, input) => sum + Math.max(0, parseInt(input.value || '0', 10) || 0), 0);
          submitBtn.disabled = hasVariantQuantities ? selectedQuantity === 0 : false;
          if (submitBtn.dataset.origHtml) {
            submitBtn.innerHTML = submitBtn.dataset.origHtml;
            delete submitBtn.dataset.origHtml;
          }
        }
      }
    });
  });
}

/* 11. Related Products Recommendation Fetcher */
function initRelatedProducts(root = document) {
  root.querySelectorAll('[data-product-recommendations]').forEach((el) => {
    const url = el.dataset.url;
    if (!url || el.dataset.loaded === 'true') return;
    el.dataset.loaded = 'true';

    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = doc.querySelector('[data-product-recommendations]');
        if (!incoming) return;
        if (incoming.querySelector('.m-product-card, .product-card')) {
          el.replaceWith(incoming);
          incoming.dataset.loaded = 'true';
          initAjaxForms(incoming);
        } else if (!el.querySelector('.m-product-card, .product-card')) {
          el.remove();
        }
      })
      .catch(() => {});
  });
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
